import { getClassNames } from '../../../_utils/classnames'
import ColorPickerPopover from '../../color-picker-popover/color-picker-popover'
import colorConvert from 'color-convert'
import React, { useMemo, useRef } from 'react'
import {
    AppMessageTemplateButtonConfig,
    MutableAppMessageTemplateButtonConfig,
} from '@pushly/models/lib/structs/app-messages/app-message-template-button-config'
import { CtaButtonType, CtaGroupTuple } from '../types'
import { titleCase } from '../../../_utils/utils'
import { AppMessageActionType } from '@pushly/models/lib/enums/app-messages/app-message-action-type'
import { BgColorsOutlined, FontColorsOutlined } from '@ant-design/icons'
import { DEFAULT_CTA_BG_COLOR, DEFAULT_CTA_FG_COLOR } from '../constants'
import { enumAsSelectOptions } from '../../../_utils/antd'
import { AppMessageActionStyle } from '@pushly/models/lib/enums/app-messages/app-message-action-style'
import { AppMessageTemplateStyle } from '@pushly/models/lib/enums'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { Position } from '@pushly/models/lib/enums/position'
import { MutableAppMessageTemplateImageConfig } from '@pushly/models/lib/structs'
import { ConditionalTooltip } from '../../conditional-popover/conditional-popover'
import { MacroType, Well } from '@pushly/aqe/lib/components'
import { Button, Form, Radio, Select, Space, Switch, Tooltip } from 'antd'
import TemplatizedTextField, {
    InputFocusRef,
} from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'

import './styles/app-message-cta-builder.scss'

type AppMessageCtaBuilderProps = {
    value: CtaGroupTuple
    type: CtaButtonType
    rootCtas: CtaGroupTuple[]
    templateStyle: AppMessageTemplateStyle
    onChange: (change: Partial<AppMessageTemplateButtonConfig>, action: CtaButtonType) => void
    disabled: boolean
}

export const AppMessageCtaBuilder = (props: AppMessageCtaBuilderProps) => {
    const { disabled, value, rootCtas, templateStyle } = props

    const [context, dispatch] = useAppMessageBuilderContext()
    const templateValue = context.variants[context.selectedVariantIdx].template

    // 2nd action not available for banners
    if (props.type === CtaButtonType.SECONDARY && templateStyle === AppMessageTemplateStyle.BANNER) return null

    const labelRef = useRef<InputFocusRef>(null)

    const handleChange = (change: Partial<AppMessageTemplateButtonConfig>) => {
        // cannot disable Primary when Secondary contains a value
        if ('enabled' in change && props.type === CtaButtonType.PRIMARY && !change.enabled && rootCtas[1][1].enabled) {
            return
        }

        props.onChange(change, props.type)
    }

    const localValue = value[1]
    const opposingAction = rootCtas[props.type === CtaButtonType.PRIMARY ? 0 : 1][1]
    const defaultValue = localValue.enabled
        ? localValue
        : ({
              enabled: true,
              fgColor: DEFAULT_CTA_FG_COLOR,
              bgColor: DEFAULT_CTA_BG_COLOR,
              action: opposingAction.enabled
                  ? opposingAction.action === AppMessageActionType.CLOSE
                      ? AppMessageActionType.OPEN_URL
                      : AppMessageActionType.CLOSE
                  : AppMessageActionType.OPEN_URL,
          } as MutableAppMessageTemplateButtonConfig)

    const [siblingCtaTuple] = rootCtas
        .filter(([type, _]) => {
            return type !== props.type
        })
        .map(([_, cta]) => cta)

    const utmMacroTypes: MacroType[] = ['domain', 'device', 'location', 'app_message', 'custom']

    const isBannerButtonUnavailable =
        templateValue &&
        templateValue.style === AppMessageTemplateStyle.BANNER &&
        templateValue?.image.enabled &&
        templateValue.image.position !== Position.LEFT

    const actionOptions = useMemo(() => {
        const options = [
            { label: 'Open URL', value: AppMessageActionType.OPEN_URL },
            {
                label: 'Prompt For Notification Permissions',
                value: AppMessageActionType.PROMPT_NOTIFICATIONS,
                disabled: siblingCtaTuple.action === AppMessageActionType.PROMPT_NOTIFICATIONS,
            },
        ]

        if (templateStyle !== AppMessageTemplateStyle.BANNER) {
            options.push({
                label: 'Dismiss',
                value: AppMessageActionType.CLOSE,
                disabled: siblingCtaTuple.action === AppMessageActionType.CLOSE,
            })
        }

        return options
    }, [templateStyle])

    const buttonToggle = (
        <ConditionalTooltip
            title="A button cannot be enabled on a Banner Message when the Image is Position Right. Please select Position Left first to enable a button"
            condition={isBannerButtonUnavailable ?? false}
        >
            <Tooltip
                title={
                    props.type === CtaButtonType.PRIMARY && rootCtas[1][1].enabled
                        ? 'Primary Button cannot be disabled while a Secondary button is enabled.'
                        : ''
                }
            >
                <label className="cta-toggle-label">
                    <Switch
                        size="small"
                        onChange={(enabled) => {
                            if (enabled) {
                                if (templateStyle === AppMessageTemplateStyle.BANNER) {
                                    const ctaKey = props.type === 'PRIMARY' ? 'ctaPrimary' : 'ctaSecondary'

                                    const update: {
                                        image: MutableAppMessageTemplateImageConfig
                                        [ctaKey: string]: MutableAppMessageTemplateButtonConfig
                                    } = {
                                        image: templateValue!.image.clone(),
                                        [ctaKey]: templateValue![ctaKey].clone({ enabled }),
                                    }
                                    if (!localValue.style) {
                                        update[ctaKey].style = AppMessageActionStyle.TAB
                                    }

                                    if (!localValue.label) {
                                        update[ctaKey].label = '›'
                                    }

                                    if (templateValue?.image.enabled && templateValue.image.position) {
                                        update.image.clone({ position: Position.LEFT })
                                    }

                                    dispatch({
                                        type: 'patch',
                                        entity: 'template',
                                        data: {
                                            image: update.image,
                                            [ctaKey]: update[ctaKey],
                                        },
                                    })
                                } else {
                                    handleChange({ ...defaultValue, enabled })
                                }

                                if (labelRef.current) {
                                    labelRef?.current.focusInput()
                                }
                            } else {
                                handleChange({ enabled })
                            }
                        }}
                        checked={localValue.enabled}
                        disabled={
                            (props.type === CtaButtonType.PRIMARY && isBannerButtonUnavailable) ||
                            (props.type === CtaButtonType.SECONDARY && !rootCtas[0][1].enabled) ||
                            disabled
                        }
                    />
                    <span className="label-with-component-l">{titleCase(props.type)} Button</span>
                </label>
            </Tooltip>
        </ConditionalTooltip>
    )

    // Progressive disclosure: only show settings when button is enabled
    const buttonSettings = localValue.enabled && (
        <div className="cta-settings-container">
            <Form.Item label="Button Color" layout="horizontal" className="cta-style-row">
                <Space size="small">
                    {templateStyle === AppMessageTemplateStyle.BANNER && (
                        <Radio.Group
                            buttonStyle="solid"
                            size="small"
                            disabled={!localValue.enabled || disabled}
                            value={localValue.style}
                            onChange={(ev) => {
                                handleChange({ style: ev.target.value })
                            }}
                        >
                            {enumAsSelectOptions(AppMessageActionStyle).map(({ value: style, label }) => (
                                <Radio.Button key={style} value={style}>
                                    {label}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    )}
                    <ColorPickerPopover
                        content={
                            <Tooltip title="Text Color" trigger="hover">
                                <Button
                                    className={getClassNames('color-popover-btn', 'cta-color-btn', {
                                        active: true,
                                    })}
                                    disabled={!localValue.enabled || disabled}
                                    style={{
                                        color:
                                            colorConvert.hex.hsl(localValue.fgColor ?? defaultValue.fgColor!)?.[2] < 70
                                                ? 'white'
                                                : 'black' ?? 'black',
                                    }}
                                >
                                    <span
                                        className="color-wrap"
                                        style={{
                                            backgroundColor: localValue.fgColor ?? defaultValue.fgColor,
                                        }}
                                    >
                                        <FontColorsOutlined />
                                    </span>
                                </Button>
                            </Tooltip>
                        }
                        disabled={!localValue.enabled || disabled}
                        defaultColor={defaultValue.fgColor!}
                        color={localValue.fgColor ?? defaultValue.fgColor!}
                        onChange={(color) => {
                            handleChange({ fgColor: color!.toString() })
                        }}
                    />
                    <ColorPickerPopover
                        content={
                            <Tooltip title="Background Color" trigger="hover">
                                <Button
                                    className={getClassNames('color-popover-btn', 'cta-color-btn', {
                                        active: true,
                                    })}
                                    disabled={!localValue.enabled || disabled}
                                    style={{
                                        color:
                                            colorConvert.hex.hsl(localValue.bgColor ?? defaultValue.bgColor!)?.[2] < 70
                                                ? 'white'
                                                : 'black' ?? 'black',
                                    }}
                                >
                                    <span
                                        className="color-wrap"
                                        style={{
                                            backgroundColor: localValue.bgColor ?? defaultValue.bgColor,
                                        }}
                                    >
                                        <BgColorsOutlined />
                                    </span>
                                </Button>
                            </Tooltip>
                        }
                        disabled={!localValue.enabled || disabled}
                        defaultColor={defaultValue.bgColor!}
                        color={localValue.bgColor ?? defaultValue.bgColor!}
                        onChange={(color) => {
                            handleChange({ bgColor: color!.toString() })
                        }}
                    />
                </Space>
            </Form.Item>

            {templateStyle !== AppMessageTemplateStyle.BANNER && (
                <Form.Item label="Button Text" className="cta-label-field">
                    <TemplatizedTextField
                        ref={labelRef}
                        className={getClassNames(null)}
                        value={localValue.label}
                        disabled={!localValue.enabled || disabled}
                        onChange={(label) => {
                            handleChange({ label })
                        }}
                        placeholder={`${titleCase(props.type)} Call-to-Action`}
                    />
                </Form.Item>
            )}

            <Form.Item label="Action Type" className="action-type-select">
                <Select<AppMessageActionType>
                    disabled={!localValue.enabled || disabled}
                    value={localValue.action ?? AppMessageActionType.OPEN_URL}
                    defaultValue={AppMessageActionType.OPEN_URL}
                    onChange={(action) => handleChange({ action })}
                    options={actionOptions}
                />
            </Form.Item>

            {localValue.action === AppMessageActionType.OPEN_URL && (
                <Form.Item label="URL" className="action-type-url">
                    <TemplatizedTextField
                        disabled={!localValue.enabled || disabled}
                        macroTypes={utmMacroTypes}
                        className={getClassNames(null, 'url-input-group', {
                            disabled: !localValue.enabled || disabled,
                        })}
                        placeholder="app://..."
                        value={localValue.url}
                        onChange={(v) => handleChange({ url: v })}
                    />
                </Form.Item>
            )}
        </div>
    )

    return (
        <Well
            variant="outline"
            padding="small"
            footerMode="hidden"
            collapsed={!localValue.enabled}
            className={getClassNames('app-message-cta-builder', {
                'cta-enabled': localValue.enabled,
                'cta-disabled': !localValue.enabled,
                'cta-primary': props.type === CtaButtonType.PRIMARY,
                'cta-secondary': props.type === CtaButtonType.SECONDARY,
            })}
            header={buttonToggle}
        >
            {buttonSettings}
        </Well>
    )
}
